import React, { useState } from 'react';
import { useInput } from 'react-admin';
import DatePicker, { registerLocale } from 'react-datepicker';
import NumberFormat from 'react-number-format';

import {
  Button,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Popover,
  TextField,
} from '@material-ui/core';
import { pt } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CloseIcon from '@material-ui/icons/Close';
import DateRangeIcon from '@material-ui/icons/DateRange';

registerLocale('ptBR', pt);

// meio-dia: o servidor usa o dia inteiro, sem depender do fuso
const meioDia = (data) =>
  new Date(data.getFullYear(), data.getMonth(), data.getDate(), 12);

const somaDias = (data, dias) => {
  const nova = new Date(data);
  nova.setDate(nova.getDate() + dias);
  return nova;
};

const chaveDia = (data) =>
  `${data.getFullYear()}-${data.getMonth()}-${data.getDate()}`;

const PRESETS = [
  { id: 'hoje', label: 'Hoje', intervalo: (h) => [h, h] },
  { id: 'ontem', label: 'Ontem', intervalo: (h) => [somaDias(h, -1), somaDias(h, -1)] },
  { id: '7dias', label: 'Últimos 7 dias', intervalo: (h) => [somaDias(h, -6), h] },
  { id: '30dias', label: 'Últimos 30 dias', intervalo: (h) => [somaDias(h, -29), h] },
  {
    id: 'mes',
    label: 'Este mês',
    intervalo: (h) => [new Date(h.getFullYear(), h.getMonth(), 1), h],
  },
  {
    id: 'mesPassado',
    label: 'Mês passado',
    intervalo: (h) => [
      new Date(h.getFullYear(), h.getMonth() - 1, 1),
      new Date(h.getFullYear(), h.getMonth(), 0),
    ],
  },
];

const dois = (n) => String(n).padStart(2, '0');
const mostrar = (d) => `${dois(d.getDate())}/${dois(d.getMonth() + 1)}/${d.getFullYear()}`;
const soDigitos = (d) => `${dois(d.getDate())}${dois(d.getMonth() + 1)}${d.getFullYear()}`;

// "ddmmaaaa" -> Date ao meio-dia, ou null se inválida
const lerData = (texto) => {
  if (!/^\d{8}$/.test(texto)) return null;
  const dia = parseInt(texto.slice(0, 2), 10);
  const mes = parseInt(texto.slice(2, 4), 10) - 1;
  const ano = parseInt(texto.slice(4), 10);
  const data = new Date(ano, mes, dia, 12);
  return data.getDate() === dia && data.getMonth() === mes ? data : null;
};

const useStyles = makeStyles((theme) => ({
  campo: { cursor: 'pointer', minWidth: 230 },
  entrada: { cursor: 'pointer' },
  painel: { width: 310, padding: theme.spacing(1, 0, 1.5) },
  personalizado: {
    display: 'flex',
    gap: 8,
    padding: theme.spacing(1.5, 2, 1),
  },
  calendario: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(0, 1),
  },
  acoes: { display: 'flex', justifyContent: 'flex-end', padding: theme.spacing(0, 2) },
}));

// Filtro de período compacto: um campo que abre um painel com atalhos e datas digitadas.
// O valor é { start, end } (Date ao meio-dia); vazio = sem filtro de período.
const PeriodoRapidoInput = (props) => {
  const classes = useStyles();
  const {
    input: { name, onChange, value },
  } = useInput(props);
  const { label = 'Período' } = props;

  const [ancora, setAncora] = useState(null);
  const [textoInicio, setTextoInicio] = useState('');
  const [textoFim, setTextoFim] = useState('');
  const [campoAtivo, setCampoAtivo] = useState(null); // 'inicio' | 'fim': qual data o calendário preenche

  const inicio = value && value.start ? new Date(value.start) : null;
  const fim = value && value.end ? new Date(value.end) : null;
  const hoje = meioDia(new Date());

  const presetAtivo = inicio && fim
    ? PRESETS.find((p) => {
        const [i, f] = p.intervalo(hoje);
        return chaveDia(i) === chaveDia(inicio) && chaveDia(f) === chaveDia(fim);
      })
    : null;

  let resumo = '';
  if (inicio && fim) {
    if (presetAtivo) resumo = presetAtivo.label;
    else if (chaveDia(inicio) === chaveDia(fim)) resumo = mostrar(inicio);
    else resumo = `${mostrar(inicio)} - ${mostrar(fim)}`;
  }

  const emitir = (novo) => onChange({ target: { name, value: novo } });

  function abrir(evento) {
    setTextoInicio(inicio ? soDigitos(inicio) : '');
    setTextoFim(fim ? soDigitos(fim) : '');
    setCampoAtivo('inicio');
    setAncora(evento.currentTarget);
  }

  function escolher(preset) {
    const [i, f] = preset.intervalo(hoje);
    emitir({ start: meioDia(i), end: meioDia(f) });
    setAncora(null);
  }

  const datasValidas = lerData(textoInicio) && lerData(textoFim) && lerData(textoInicio) <= lerData(textoFim);

  function aplicar() {
    if (!datasValidas) return;
    emitir({ start: lerData(textoInicio), end: lerData(textoFim) });
    setAncora(null);
  }

  function escolherData(data) {
    const nova = meioDia(data);
    if (campoAtivo === 'inicio') {
      setTextoInicio(soDigitos(nova));
      const fimAtual = lerData(textoFim);
      if (!fimAtual || fimAtual < nova) setTextoFim(soDigitos(nova));
      setCampoAtivo('fim');
    } else {
      setTextoFim(soDigitos(nova));
      const inicioAtual = lerData(textoInicio);
      if (!inicioAtual || inicioAtual > nova) setTextoInicio(soDigitos(nova));
    }
  }

  const campoData = (rotulo, texto, setTexto, campo) => (
    <NumberFormat
      customInput={TextField}
      label={rotulo}
      variant="outlined"
      margin="dense"
      format="##/##/####"
      mask="_"
      placeholder="dd/mm/aaaa"
      value={texto}
      onValueChange={({ value: v }) => setTexto(v)}
      InputLabelProps={{ shrink: true }}
      onFocus={() => setCampoAtivo(campo)}
      focused={campoAtivo === campo}
      error={texto.length === 8 && !lerData(texto)}
    />
  );

  return (
    <>
      <TextField
        className={classes.campo}
        label={label}
        variant="outlined"
        margin="dense"
        value={resumo}
        placeholder="Todo o período"
        onClick={abrir}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          readOnly: true,
          classes: { input: classes.entrada },
          startAdornment: (
            <InputAdornment position="start">
              <DateRangeIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {resumo && (
                <IconButton
                  size="small"
                  aria-label="Remover filtro de período"
                  onClick={(e) => {
                    e.stopPropagation();
                    emitir('');
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <ArrowDropDownIcon />
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={Boolean(ancora)}
        anchorEl={ancora}
        onClose={() => setAncora(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className={classes.painel}>
          <List dense disablePadding>
            {PRESETS.map((preset) => (
              <ListItem
                key={preset.id}
                button
                selected={Boolean(presetAtivo && presetAtivo.id === preset.id)}
                onClick={() => escolher(preset)}
              >
                <ListItemText primary={preset.label} />
              </ListItem>
            ))}
            <ListItem
              button
              selected={!resumo}
              onClick={() => {
                emitir('');
                setAncora(null);
              }}
            >
              <ListItemText primary="Todo o período (sem filtro)" />
            </ListItem>
          </List>
          <Divider />
          <div className={classes.personalizado}>
            {campoData('De', textoInicio, setTextoInicio, 'inicio')}
            {campoData('Até', textoFim, setTextoFim, 'fim')}
          </div>
          {campoAtivo && (
            <div className={classes.calendario}>
              <DatePicker
                inline
                locale="ptBR"
                selected={lerData(campoAtivo === 'inicio' ? textoInicio : textoFim)}
                openToDate={lerData(campoAtivo === 'inicio' ? textoInicio : textoFim) || hoje}
                onChange={escolherData}
                startDate={lerData(textoInicio)}
                endDate={lerData(textoFim)}
                selectsStart={campoAtivo === 'inicio'}
                selectsEnd={campoAtivo === 'fim'}
              />
            </div>
          )}
          <div className={classes.acoes}>
            <Button size="small" color="primary" variant="contained" disabled={!datasValidas} onClick={aplicar}>
              Aplicar
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default PeriodoRapidoInput;
